import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import CollapseBlock from '../../../../../common/blocks/CollapseBlock/CollapseBlock.jsx';
import FormTextField from '../../../../../antd/form/FormTextField/FormTextField.jsx';
import FormSelect from '../../../../../antd/form/FormSelect/FormSelect.jsx';
import { required } from '../../../../../antd/form/FormValidation.js';
import FormTextEditor from '../../../../../antd/form/FormTextEditor/FormTextEditor.jsx';
import { Col, Form as AntdForm, InputNumber, Row } from 'antd';
import FormItem from 'antd/es/form/FormItem';
import FormButton from '../../../../../antd/form/FormButton/FormButton.jsx';
import FormButtonsContainer from '../../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormDate from '../../../../../antd/form/FormDate/FormDate.jsx';
import FormSwitch from '../../../../../antd/form/FormSwitch/FormSwitch.jsx';
import Form from '../../../../../antd/form/Form/Form.jsx';
import dayjs from 'dayjs';
import { QuestionModal } from '../../../../../common/index.jsx';
import useUserHasRole from '../../../../../../hooks/useUserHasRoles.jsx';
import { ROLE_FRI_JOBS_ADMIN } from '../../../../../../utils/constants/roles.js';
import { allAllowedTypes } from '../../../../../../hooks/useFileDraggerFileValidation.jsx';

const FjJobForm = ({
  initialValues,
  archiveMode,
  onSubmit,
  studyPrograms,
  jobsTypes,
  partners,
}) => {
  const [form] = AntdForm.useForm();
  const { getFieldValue } = form;
  const { t } = useTranslation();
  const isFriJobsAdmin = useUserHasRole([ROLE_FRI_JOBS_ADMIN]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [answerYes, setAnswerYes] = useState(false);

  const onFinish = (values) => {
    const payload = {
      ...values,
      date_from: values?.date_from
        ? dayjs(values.date_from).format('YYYY-MM-DD')
        : undefined,
      date_to: values?.date_to
        ? dayjs(values.date_to).format('YYYY-MM-DD')
        : undefined,
    };

    if (answerYes || isFriJobsAdmin || !initialValues.approved_at) {
      onSubmit(payload);
    } else {
      setIsModalVisible(true);
    }
  };

  const studyProgramsOptions = useMemo(
    () =>
      studyPrograms &&
      studyPrograms.map((item) => ({
        value: item.id,
        label: item.name + ' (' + item.study_program_type.shortcut + ')',
      })),
  );

  const jobsTypesOptions = useMemo(
    () =>
      jobsTypes &&
      jobsTypes.map((item) => ({
        value: item.id,
        label: item.name,
      })),
  );

  const partnersOptions = useMemo(
    () =>
      partners &&
      partners.map((item) => ({
        value: item.id,
        label: item.name,
      })),
  );

  const watchFrom = AntdForm.useWatch('date_from', form);

  const handleOk = () => {
    setAnswerYes(true);
    form.submit();
  };

  const disabledDateTo = (current) => {
    if (!watchFrom) {
      return current.isBefore(dayjs(), 'day');
    }
    return watchFrom.isAfter(current, 'day'); // Disable all dates before day of 'from' date
  };

  const disabledDateFrom = (current) => {
    return current && current.isBefore(dayjs(), 'day');
  };

  initialValues.date_from = initialValues.date_from
    ? dayjs(initialValues.date_from, 'YYYY-MM-DD')
    : undefined;
  initialValues.date_to = initialValues.date_to
    ? dayjs(initialValues.date_to, 'YYYY-MM-DD')
    : undefined;

  if (archiveMode) {
    initialValues.date_from = undefined;
    initialValues.date_to = undefined;
  }

  const fromBeforeToValidator = (_, from) => {
    var to = getFieldValue('date_to');
    if (from != null && to != null && to < from) {
      return Promise.reject(t('Začiatok musí byť skôr ako koniec'));
    } else {
      return Promise.resolve();
    }
  };

  // Max 3 months difference between date_from and date_to
  const maxThreeMonthsValidator = (_, to) => {
    const from = getFieldValue('date_from');
    if (!from || !to) return Promise.resolve();
    const maxTo = from.add(3, 'month').endOf('day');
    if (to.isAfter(maxTo)) {
      return Promise.reject(
        t('Maximálna doba zobrazenia pracovnej ponuky sú 3 mesiace'),
      );
    }
    return Promise.resolve();
  };

  return (
    <>
      <Form
        form={form}
        onFinish={onFinish}
        initialValues={initialValues}
        layout="vertical"
      >
        <CollapseBlock title={t('Základné informácie')} collapsible>
          {isFriJobsAdmin && (
            <FormSelect
              label={t('Názov externého partnera FRI')}
              name="external_partner_id"
              placeholder={t('Vyberte externého partnera FRI')}
              options={partnersOptions}
              rules={[required(t)]}
            />
          )}

          <FormTextField
            label={t('Názov pracovnej ponuky')}
            name="title"
            rules={[required(t)]}
          />

          <FormSelect
            label={t('Typ pracovnej ponuky')}
            name="job_types_id"
            placeholder={t('Vyberte typ pracovnej ponuky')}
            options={jobsTypesOptions}
            mode={'multiple'}
            rules={[required(t)]}
          />

          <FormSelect
            label={t('Študijné programy')}
            name="study_programs_id"
            placeholder={t('Vyhľadať študijné programy')}
            options={studyProgramsOptions}
            mode={'multiple'}
            rules={[required(t)]}
          />

          <FormTextEditor
            label={t('Detail ponuky')}
            name="description"
            imgUploadDirectory={'fri_jobs'}
            fileUploadDirectory={'fri_jobs'}
            allowedTypes={allAllowedTypes}
            enableCustomFileName={true}
            rules={[required(t)]}
          />
        </CollapseBlock>

        <CollapseBlock title={t('Pracovné podmienky')} collapsible>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <FormTextField
                label={t('Miesto práce')}
                name="place_of_work"
                rules={[required(t)]}
              />
            </Col>
            <Col xs={24} md={12}>
              <FormTextField
                label={t('Ponúkaný plat (text)')}
                name="salary"
                rules={[required(t)]}
              />
            </Col>
            <Col xs={24} md={12}>
              <FormTextField
                label={t('Nástup od (text)')}
                name="date_of_start"
                rules={[required(t)]}
              />
            </Col>
            <Col xs={24} md={12}>
              <FormItem
                label={t('Počet pozícií')}
                name="capacity"
                rules={[required(t)]}
              >
                <InputNumber name="capacity" type={'number'} min={1}></InputNumber>
              </FormItem>
            </Col>
          </Row>
        </CollapseBlock>

        <CollapseBlock title={t('Zverejnenie')} collapsible>
          <FormItem
            label={t('Doba zobrazenia pracovnej ponuky')}
            rules={[required(t)]}
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} md={8}>
                <FormDate
                  name="date_from"
                  label={t('Od')}
                  defaultValue={initialValues.date_from}
                  rules={[{ validator: fromBeforeToValidator }, required(t)]}
                  onChange={() => {
                    // Revalidate 'to' when 'from' changes for max-range enforcement
                    form.validateFields(['date_to']);
                  }}
                  disabledDate={disabledDateFrom}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <FormDate
                  name="date_to"
                  label={t('Do')}
                  rules={[required(t), { validator: maxThreeMonthsValidator }]}
                  onChange={() => {
                    // Revaliduj pole 'from', keď sa zmení 'to'
                    form.validateFields(['date_from']);
                  }}
                  defaultValue={initialValues.date_to}
                  disabledDate={disabledDateTo}
                />
              </Col>
            </Row>
          </FormItem>

          <FormSwitch
            label={t('Posielať notifikácie o prihlásení študentov')}
            name={'is_interest_notification_enabled'}
          />

          {isFriJobsAdmin && (
            <FormSwitch label={t('Zobrazovať navrchu')} name={'top'} />
          )}
        </CollapseBlock>

        <FormButtonsContainer>
          <FormButton submitOnClick={true} submitKey={'friJobs'}>
            {t('Uložiť')}
          </FormButton>
        </FormButtonsContainer>
      </Form>

      <QuestionModal
        chosenPositive={handleOk}
        question={
          archiveMode
            ? t(
                'Pracovná ponuka sa presunie do zoznamu ponúk čakajúcich na schválenie. O ďalšom postupe Vás budem informovať.',
              )
            : t(
                'Naozaj chcete vykonať zmeny v pracovnej ponuke? Pracovná ponuka bude pozastavená a poslaná na opätovné schválenie!',
              )
        }
        visible={isModalVisible}
        yes={t('Áno')}
        toggle={() => setIsModalVisible(false)}
        headerTitle={t('Zmeniť pracovnú ponuku')}
      />
    </>
  );
};

FjJobForm.prototype = {
  initialValues: PropTypes.object.isRequired,
  isCreate: PropTypes.bool.isRequired,
  archiveMode: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  studyPrograms: PropTypes.array.isRequired,
  jobsTypes: PropTypes.array.isRequired,
  partners: PropTypes.array.isRequired,
};

export default FjJobForm;
